import { useEffect, useRef, useState } from "react";
import { X, Share2, Copy, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * A whimsical "we colored with M.K. Bunch!" share card that appears
 * after a family unlocks a coloring ebook. Uses the browser's
 * Web Share API on mobile and falls back to copy-to-clipboard on desktop.
 * Also lets the user download the card as an image via html-to-image
 * (not currently installed — we do a canvas-based fallback).
 */
export default function ColoringShareModal({ open, onClose, book }) {
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (open) {
      // reset scroll lock on body
      const orig = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = orig; };
    }
  }, [open]);

  if (!open || !book) return null;

  const shareUrl = `${window.location.origin}/coloring/${book.id}`;
  const caption = `We colored with M.K. Bunch! 🎨\n\n"${book.title}" coloring ebook — a cozy little activity from mkbunchbooks.com. Grab yours: ${shareUrl}`;

  const doShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `We colored with M.K. Bunch — ${book.title}`,
          text: caption,
          url: shareUrl,
        });
        toast.success("Shared! 💌");
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(caption);
        toast.success("Caption copied — paste it into your favorite feed 💌");
      } catch {
        toast.error("Couldn't copy — try selecting the text manually.");
      }
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption copied 💌");
    } catch {
      toast.error("Couldn't copy — try selecting the text manually.");
    }
  };

  const downloadCard = async () => {
    setBusy(true);
    try {
      const svgEl = cardRef.current?.querySelector("svg");
      if (!svgEl) throw new Error("No card");
      const s = new XMLSerializer().serializeToString(svgEl);
      const svgBlob = new Blob([s], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
      const canvas = document.createElement("canvas");
      canvas.width = 1080; canvas.height = 1080;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, 1080, 1080);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const dl = document.createElement("a");
        dl.href = URL.createObjectURL(blob);
        dl.download = `mkbunchbooks-${(book.title || "coloring").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-share.png`;
        dl.click();
        setTimeout(() => URL.revokeObjectURL(dl.href), 1000);
      }, "image/png");
      URL.revokeObjectURL(url);
      toast.success("Card downloaded 📎");
    } catch {
      toast.error("Couldn't download the card. Try the Share button instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(45,48,85,0.72)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="coloring-share-modal"
    >
      <div
        className="w-full max-w-md rounded-3xl overflow-hidden"
        style={{ background: "#FFFFFF", boxShadow: "0 40px 80px rgba(45,48,85,0.35)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative p-5 sm:p-6">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full inline-flex items-center justify-center transition-colors hover:bg-[#FFE1EC]"
            style={{ color: "#7E82A1" }}
            aria-label="Close"
            data-testid="coloring-share-close"
          >
            <X size={18} />
          </button>

          <div className="mb-3">
            <span className="font-caveat text-2xl" style={{ color: "#FF6B9D" }}>
              you unlocked something magical
            </span>
            <h2 className="font-fredoka text-2xl leading-tight" style={{ color: "#2D3055" }}>
              Share the little joy?
            </h2>
          </div>

          {/* The shareable card itself */}
          <div ref={cardRef} className="rounded-2xl overflow-hidden" style={{ border: "1px solid #F0EBFF" }}>
            <svg viewBox="0 0 1080 1080" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-label={`We colored with M.K. Bunch — ${book.title}`}>
              <defs>
                <linearGradient id="shareBg" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FFF3F8" />
                  <stop offset="55%" stopColor="#FFF8E7" />
                  <stop offset="100%" stopColor="#F0EBFF" />
                </linearGradient>
                <radialGradient id="shareGlow" cx="50%" cy="30%" r="60%">
                  <stop offset="0%" stopColor="#FFF4C2" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#FFF4C2" stopOpacity="0" />
                </radialGradient>
              </defs>
              <rect width="1080" height="1080" fill="url(#shareBg)" />
              <rect width="1080" height="1080" fill="url(#shareGlow)" />
              {/* Twinkles */}
              <g fill="#FFB347" opacity="0.75">
                <circle cx="120" cy="140" r="6" />
                <circle cx="960" cy="220" r="8" />
                <circle cx="900" cy="880" r="6" />
                <circle cx="140" cy="920" r="7" />
              </g>
              <g fill="#FF6B9D" opacity="0.85">
                <path d="M 200 320 l 10 -22 l 10 22 l 22 10 l -22 10 l -10 22 l -10 -22 l -22 -10 z" />
                <path d="M 860 640 l 8 -18 l 8 18 l 18 8 l -18 8 l -8 18 l -8 -18 l -18 -8 z" />
              </g>
              {/* Big pink caveat */}
              <text
                x="540" y="230"
                textAnchor="middle"
                fontFamily="Caveat, cursive"
                fontSize="56"
                fill="#FF6B9D"
              >we colored with</text>
              <text
                x="540" y="330"
                textAnchor="middle"
                fontFamily="Fredoka, sans-serif"
                fontSize="80"
                fontWeight="700"
                fill="#2D3055"
              >M.K. Bunch</text>
              {/* Book title */}
              <foreignObject x="120" y="380" width="840" height="200">
                <div xmlns="http://www.w3.org/1999/xhtml" style={{
                  textAlign: "center", fontFamily: "'Fredoka', sans-serif",
                  fontSize: 42, color: "#2D3055", fontWeight: 500, lineHeight: 1.25,
                }}>
                  <span style={{ background: "#FFFFFF", padding: "10px 24px", borderRadius: 999, boxShadow: "0 6px 16px rgba(45,48,85,0.10)" }}>
                    "{book.title}"
                  </span>
                </div>
              </foreignObject>
              {/* Book cover thumb */}
              <foreignObject x="365" y="600" width="350" height="330">
                <div xmlns="http://www.w3.org/1999/xhtml" style={{
                  width: "100%", height: "100%", borderRadius: 26, overflow: "hidden",
                  boxShadow: "0 20px 50px rgba(45,48,85,0.22)",
                  border: "6px solid #FFFFFF",
                  transform: "rotate(-2deg)",
                }}>
                  <img
                    src={book.cover_image_url}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    crossOrigin="anonymous"
                  />
                </div>
              </foreignObject>
              {/* Footer */}
              <text x="540" y="1000" textAnchor="middle" fontFamily="Fredoka, sans-serif" fontSize="30" fontWeight="600" fill="#2D3055">
                MKBunchBooks<tspan fill="#FF6B9D">.com</tspan>
              </text>
              <text x="540" y="1040" textAnchor="middle" fontFamily="Caveat, cursive" fontSize="30" fill="#7E82A1">
                adventures &amp; wonders for little hearts
              </text>
            </svg>
          </div>

          <div className="mt-4 space-y-2">
            <button
              type="button"
              onClick={doShare}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-fredoka text-sm text-white transition-transform hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg,#FF6B9D 0%,#FF8C42 100%)",
                boxShadow: "0 10px 24px rgba(255,140,66,0.30)",
              }}
              data-testid="coloring-share-btn"
            >
              <Share2 size={16} /> Share it
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={copyCaption}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-fredoka text-sm transition-transform hover:-translate-y-0.5"
                style={{ background: "#FFFFFF", color: "#2D3055", border: "1.5px solid #E4DFF6" }}
                data-testid="coloring-share-copy"
              >
                <Copy size={14} /> Copy caption
              </button>
              <button
                type="button"
                onClick={downloadCard}
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-fredoka text-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                style={{ background: "#FFFFFF", color: "#2D3055", border: "1.5px solid #E4DFF6" }}
                data-testid="coloring-share-download"
              >
                <Download size={14} /> {busy ? "Saving…" : "Save card"}
              </button>
            </div>
          </div>

          <p className="mt-4 text-center text-xs font-nunito" style={{ color: "#7E82A1" }}>
            <Sparkles size={12} className="inline" /> Every share brings another little heart to the storybook woods.
          </p>
        </div>
      </div>
    </div>
  );
}
